<script lang="ts">
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import type { Distributor } from "@shared/types/Distributor";
  import type { RetailerDistributorSummary } from "@shared/types/RetailerDistributor";
  import type { RetailerUser } from "@shared/types/User";
  import type { SalesRep } from "@shared/types/SalesRep";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import ConnectDistributorModal from "@retailer/svelte/Settings/DistributorContacts/ConnectDistributorModal/ConnectDistributorModal.svelte";
  import AddRepModalService from "@shared/svelte/Modules/AddRepModalService";
  import AddRepWithSalesRepList from "@retailer/svelte/Shared/AddRepWithSalesRepList.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import CloudinaryImageService from "@shared/svelte/Modules/CloudinaryImageService";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { DeliveryTruckSolid } from "@getprovi/craft";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { getStockSiteLocation } from "@shared/services/retailer_user_authentication_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";
  import { EventTracking } from "@shared/services/event_tracking/events";

  export let user: RetailerUser;
  export let Distributors: any; // angular service

  let retailerDistributors: RetailerDistributorSummary[] = [];
  let showConnectDistributorModal = false;
  let selectedDistributor: Distributor = null;
  let selectedReps: SalesRep[] = [];

  const findDistributor = retailerDistributor => {
    return $DistributorsStore.find(d => d.id == retailerDistributor.distributor.id);
  };

  const fetchDistributors = async () => {
    try {
      retailerDistributors = await HttpPromise({
        method: "GET",
        url: "api/retailer/retailer_distributors",
        data: {
          limit: 5,
        },
      });
    } catch (_) {
      Flash.now({
        type: "error",
        message: "There was a problem loading your distributors",
      });
    }
  };
  fetchDistributors();

  const service = new AddRepModalService();

  const submit = service.submit((distributor, addedReps) => {
    const index = retailerDistributors.findIndex(rd => rd.distributor.id === distributor.id);
    retailerDistributors[index].distributor = {
      ...retailerDistributors[index].distributor,
      sales_reps: addedReps,
    };

    DistributorsStore.pushAddedSalesReps(distributor.id, addedReps);
    window["stateService"].reload();
  });

  function handleEvent(event) {
    selectedDistributor = event.detail;
    showConnectDistributorModal = true;
  }

  $: if (!showConnectDistributorModal) {
    selectedReps = [];
  }

  $: distributorMap = retailerDistributors.reduce((map, rd) => {
    map[rd.id] = findDistributor(rd);
    return map;
  }, {});

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let authService = getControlStateAuthenticationService();

  let authStatus: string;
  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
  );

  $: if (hasRetailerUserAuthRollout && authService) {
    for (let rd of retailerDistributors) {
      rd.retailerAuthStatus = authService?.getRetailerUserAuthenticationStatus?.(
        $RetailerUserAuthenticationsStore,
        [rd.distributor.id],
      );
    }

    retailerDistributors = retailerDistributors.filter(
      rd => !rd.retailerAuthStatus || rd.retailerAuthStatus == "valid",
    );

    if (authStatus == "pending") {
      retailerDistributors = retailerDistributors?.slice(0, 3);
    }
  }

  let innerWidth: number | null;

  const { trackClick } = EventTracking;
</script>

<style>
  a.button-link {
    color: #fff;
    text-decoration: none;
  }

  #title-bar {
    background: linear-gradient(#f2f7fb 65%, transparent 65%);
    background-color: #f2f7fb;
    color: #000;
  }

  #main {
    margin-top: -3rem;
  }
</style>

<svelte:window bind:innerWidth />

<FizzStyles>
  <div>
    <div>
      <div class="fizz-layout-one-col-title-bar">
        <section data-grid-area="title-bar" id="title-bar">
          <div class="fizz-container">
            <div
              class="fizz-space-between-responsive fizz-inset-vertical-24 fizz-border-bottom fizz-stack-16"
            >
              <div>
                <h2 class="fizz-heading-2 fizz-stack-4">Welcome to Provi, {user.first_name}!</h2>
                <p class="fizz-text-medium">
                  You&apos;re all setup to send orders to your distributor sales reps
                </p>
              </div>
              <button id="retailer-training-link" class="fizz-button-primary fizz-max-content">
                <a
                  class="button-link"
                  href="/sc/gettingstarted"
                  on:click={() => trackClick("first_order_bar_get_started_with_provi_button")}
                >
                  Get started with Provi
                </a>
              </button>
            </div>
            <div class="fizz-flex-row fizz-space-between fizz-align-content-start">
              <h2
                class="fizz-stack-16 fizz-inset-vertical-8"
                class:fizz-heading-3={!$LayoutStore.isMobile}
              >
                Browse your distributor catalogs
              </h2>
              <UIRouterLink
                stateName={"layout.distributors"}
                classes="fizz-inset-vertical-8 fizz-text-strong"
                useTrackClick
                eventName="click"
                targetObjectType="first_order_bar_see_all_distributors_button"
              >
                {#if $LayoutStore.isMobile}
                  See all
                {:else}
                  See all distributors
                {/if}
              </UIRouterLink>
            </div>
          </div>
        </section>
        <main data-grid-area="main" id="main">
          <div class="fizz-container">
            <div class:fizz-flex-row={!$LayoutStore.isMobile} class="fizz-align-content-start">
              {#if authService && authService?.ctaComponent && authStatus == "pending" && innerWidth >= 1337}
                <div class="fizz-inset-square-16" style="padding-right: 0; margin-right: 0;">
                  <div
                    style=" height: 262px;"
                    class="fizz-bordered fizz-inset-squish-24 fizz-card fizz-flex-row fizz-justify-center"
                  >
                    <svelte:component this={authService?.ctaComponent} {authStatus} {authService} />
                  </div>
                </div>
              {/if}
              <div
                class="fizz-flex-item fizz-grid fizz-inset-square-16"
                style="--grid-col-min: 293px; --grid-col-max: 400px; --gap: 16px; grid-auto-rows: minmax(123px, 1fr)"
              >
                {#if authService && authService?.ctaComponent && authStatus == "pending" && innerWidth < 1337}
                  <div
                    class="fizz-inset-square-16 fizz-card fizz-bordered fizz-inset-square-8 fizz-flex-row-0"
                  >
                    <svelte:component this={authService?.ctaComponent} {authStatus} {authService} />
                  </div>
                {/if}
                {#each retailerDistributors as retailerDistributor}
                  {#if !!distributorMap[retailerDistributor.id]}
                    <div
                      class="fizz-card fizz-card-interactive fizz-bordered fizz-inset-square-8 fizz-flex-row-0"
                      id="distributor_{retailerDistributor.distributor.id}"
                    >
                      <!-- svelte-ignore a11y-missing-content -->
                      <UIRouterLink
                        classes="fizz-overlay-link"
                        stateName={"layout.product_listing"}
                        params={{ distributors: retailerDistributor.distributor.id }}
                      />
                      <figure class="fizz-flex-row-0">
                        <div
                          class="fizz-flex-row fizz-justify-center fizz-flex-no-shrink"
                          style="width: 100px"
                        >
                          {#if distributorMap[retailerDistributor.id].cloudinary_image_url}
                            <img
                              on:error={() =>
                                (distributorMap[retailerDistributor.id].cloudinary_image_url =
                                  null)}
                              class="logo"
                              src={CloudinaryImageService.getUrl(
                                distributorMap[retailerDistributor.id].cloudinary_image_url,
                                75,
                                75,
                              )}
                              alt="Logo"
                            />
                          {:else}
                            <div
                              class="p-16 bg-brand rounded-sm"
                              style="height: 75px; width: 75px;"
                            >
                              <DeliveryTruckSolid fill="white" />
                            </div>
                          {/if}
                        </div>
                        <figcaption style="min-width: 0">
                          <p class="fizz-text-large">
                            <strong>{retailerDistributor.distributor.name} </strong>
                          </p>

                          {#if !retailerDistributor.retailerAuthStatus || (retailerDistributor.retailerAuthStatus == "valid" && retailerDistributor.distributor.id !== OHLQ_DISTRIBUTOR_ID)}
                            <AddRepWithSalesRepList
                              distributor={distributorMap[retailerDistributor.id]}
                              {retailerDistributor}
                              maxReps={1}
                              source="first_order_bar"
                              on:openConnectDistributorModal={handleEvent}
                            />
                          {:else if retailerDistributor.retailerAuthStatus == "valid" && retailerDistributor.distributor.id === OHLQ_DISTRIBUTOR_ID}
                            {#await getStockSiteLocation(retailerDistributor.distributor.id, retailerDistributor.account_number) then data}
                              <strong>My Agency:</strong>
                              <UIRouterLink stateName={"layout.accounts"}>
                                <strong>
                                  {data.name || ""}
                                </strong>
                              </UIRouterLink>
                            {/await}
                          {/if}
                        </figcaption>
                      </figure>
                    </div>
                  {/if}
                {/each}

                <a
                  href="/settings/accounts"
                  class="fizz-card fizz-card-interactive fizz-bordered fizz-flex-row fizz-inset-square-8 fizz-justify-center"
                  on:click={() => trackClick("first_order_bar_add_more_distributors_button")}
                >
                  <p class="fizz-flex-row-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 32 32"
                      class="fizz-icon-24"
                    >
                      <path d="M26 17.4h-8.6V26h-2.9v-8.6H6v-2.9h8.6V6h2.9v8.6H26v2.8z" />
                    </svg>
                    <strong>Add more distributors to your account </strong>
                  </p>
                </a>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</FizzStyles>

<ConnectDistributorModal
  bind:showConnectDistributorModal
  bind:selectedDistributor
  bind:selectedReps
  {submit}
  addNewSalesRep={Distributors.addNewSalesRep}
  providedDistributor={true}
  addSalesRepErrorCallback={service.addSalesRepErrorCallback}
  submitErrorCallback={service.errorCallback}
/>
